import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { writings } from './data/writings';

// Your birthday (28 Apr 2004, UTC) - moved outside component to prevent recreation
const BIRTH = new Date('2004-04-28T00:00:00Z');

const Home: React.FC = () => {
  const [daysAlive, setDaysAlive] = useState<string>('');
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  // Days alive calculation effect
  useEffect(() => {
    const tick = () => {
      const elapsedMs = Date.now() - BIRTH.getTime();
      const daysAlive = Math.floor(elapsedMs / (1000 * 60 * 60 * 24));
      setDaysAlive(daysAlive.toLocaleString());
      requestAnimationFrame(tick);
    };
    
    tick();
  }, []);

  // Load effect for blur animation
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);


  return (
    <div className="min-h-screen bg-[#0D1010] text-[#F0EDE8] font-geist">
      {/* Main Content */}
      <div className={`min-h-screen flex flex-col justify-center px-4 sm:px-6 lg:px-12 max-w-5xl mx-auto py-8 sm:py-0 transition-all duration-1000 ease-out ${
        isLoaded ? 'blur-none opacity-100' : 'blur-lg opacity-0'
      }`}>
        {/* Introduction Section */}
        <section className="mb-8 sm:mb-12">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-crimson font-normal mb-6 sm:mb-8 leading-tight">
            Nice to meet you, my name is Touko.
          </h1>
          
          <p className="text-base sm:text-lg lg:text-xl text-[#F0EDE8]/60 leading-relaxed mb-6 sm:mb-8">
            i have been alive for <span className="inline-block font-geist">{daysAlive}</span> days (hoping for ∞), and i enjoy building things and running ultramarathons.
          </p>
          
          <p className="text-base sm:text-lg lg:text-xl text-[#F0EDE8]/60 leading-relaxed mb-6 sm:mb-8">
            currently working on developing superior technological capabilities for Europe and our allies at{' '}     
            <a
              href="https://kovahq.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#F0EDE8]/60 font-normal underline underline-offset-4 hover:text-[#F0EDE8] transition-colors duration-200 cursor-pointer"
            >
              Kova Defence
            </a>.
          </p>
          
          <p className="text-base sm:text-lg lg:text-xl text-[#F0EDE8]/60 leading-relaxed mb-6 sm:mb-8">
            previously, i built software startups and worked on operations and technology at{' '}
            <a
              href="https://capaloai.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#F0EDE8]/60 font-normal underline underline-offset-4 hover:text-[#F0EDE8] transition-colors duration-200 cursor-pointer"
            >
              Capalo AI
            </a>.
          </p>
          
          <p className="text-base sm:text-lg lg:text-xl text-[#F0EDE8]/60 leading-relaxed">
            worked in & led projects that raised hundreds of thousands €€ at{' '}
            <a
              href="https://tamperees.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#F0EDE8]/60 font-normal underline underline-offset-4 hover:text-[#F0EDE8] transition-colors duration-200 cursor-pointer"
            >
              tamperees
            </a>
          </p>

          <p className="text-base sm:text-lg lg:text-xl text-[#F0EDE8]/60 leading-relaxed mt-6 sm:mt-8">
            to get in contact call me, and if you dont have my number then dont
          </p>
        </section>

        {/* Links Section */}
        <section className="mb-12 sm:mb-16">
          <div className="flex flex-col sm:flex-row flex-wrap gap-4 sm:gap-6">
            <a
              href="https://twitter.com/RautiainenTouko"
              target="_blank"
              rel="noopener noreferrer"
              className="text-base sm:text-lg text-[#F0EDE8]/60 hover:text-[#F0EDE8] transition-colors duration-200 underline underline-offset-4"
            >
              twitter
            </a>
            <a
              href="https://linkedin.com/in/toukorautiainen"
              target="_blank"
              rel="noopener noreferrer"
              className="text-base sm:text-lg text-[#F0EDE8]/60 hover:text-[#F0EDE8] transition-colors duration-200 underline underline-offset-4"
            >
              linkedin
            </a>
            <a
              href="https://github.com/stoget1n"
              className="text-base sm:text-lg text-[#F0EDE8]/60 hover:text-[#F0EDE8] transition-colors duration-200 underline underline-offset-4"
            >
              GitHub
            </a>
          </div>
        </section>

        {/* Writings Section */}
        <section>
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-crimson mb-6 sm:mb-8">/thoughts/spam/publish</h2>
          <div className="space-y-4">
            {writings.map((writing) => (
              <Link
                key={writing.id}
                to={`/blog/${writing.id}`}
                className="block text-left w-full hover:text-[#F0EDE8]/80 transition-colors duration-200"
              >
                <h3 className="text-lg sm:text-xl font-crimson text-[#F0EDE8]/60 hover:text-[#F0EDE8] transition-colors duration-200">
                  {writing.title}
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  {writing.publishedAt && (
                    <span className="text-xs text-[#F0EDE8]/40 font-geist-mono">
                      {new Date(writing.publishedAt).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </span>
                  )}
                  {writing.tags && writing.tags.length > 0 && (
                    <>
                      <span className="text-xs text-[#F0EDE8]/20">•</span>
                      {writing.tags.map((tag, index) => (
                        <span key={index} className="text-xs text-[#F0EDE8]/40 font-geist-mono">
                          {tag}
                        </span>
                      ))}
                    </>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Home;
